import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  UserPlus, 
  Edit, 
  Trash2, 
  ArrowLeft, 
  Plus, 
  X, 
  Briefcase 
} from 'lucide-react';
import { users, companyRoles } from '../services/api';
import Header from '../components/layout/Header';
import { useTranslation } from 'react-i18next';
import UserModal from '../components/users/UserModal';

const CompanyRoleModal = ({ isOpen, onClose, role, onSave, onDelete }) => {
  const { t } = useTranslation();
  const [roleData, setRoleData] = useState({
    name: '',
    description: '',
    ...role
  });

  const [error, setError] = useState('');

  const validateForm = () => {
    if (!roleData.name) {
      setError(t('common.validation.required'));
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(roleData);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">
            {role ? t('userManagement.editCompanyRole') : t('userManagement.newCompanyRole')}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('userManagement.roleName')} *</label>
            <input
              type="text"
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                error ? 'border-red-500' : ''
              }`}
              value={roleData.name}
              onChange={(e) => setRoleData({ ...roleData, name: e.target.value })}
            />
            {error && (
              <p className="mt-1 text-sm text-red-500">{error}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">{t('userManagement.roleDescription')}</label>
            <textarea
              rows="3"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              value={roleData.description}
              onChange={(e) => setRoleData({ ...roleData, description: e.target.value })}
            />
          </div>

          <div className="flex justify-between space-x-3 pt-4">
            {role && (
              <button
                type="button"
                onClick={() => onDelete(role._id)}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
              >
                {t('common.actions.delete')}
              </button>
            )}
            <div className="flex space-x-3 ml-auto">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                {t('common.actions.cancel')}
              </button>
              <button
                type="submit"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                {role ? t('common.actions.update') : t('common.actions.create')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

const UserManagementPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [usersList, setUsersList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [companyRolesList, setCompanyRoles] = useState([]);
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'roles'

  useEffect(() => {
    fetchUsers();
    loadCompanyRoles();
  }, []);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const { data } = await users.getAll();
      console.log('User data from API:', JSON.stringify(data, null, 2));
      
      // Check specifically for companyRole structure
      if (data.length > 0) {
        console.log('Sample user companyRole:', data[0].companyRole);
        console.log('Type of companyRole:', typeof data[0].companyRole);
        if (data[0].companyRole) {
          console.log('Company role keys:', Object.keys(data[0].companyRole));
        }
      }
      
      setUsersList(data);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.response?.data?.message || t('userManagement.errors.loadUsers'));
    } finally {
      setIsLoading(false);
    }
  };

  const loadCompanyRoles = async () => {
    try {
      console.log('Fetching company roles from API...');
      const { data } = await companyRoles.getAll();
      console.log('Received company roles:', data);
      setCompanyRoles(data);
    } catch (err) {
      console.error('Error loading company roles:', err);
      // If API call fails, we'll have an empty array rather than using default roles
      setCompanyRoles([]);
    }
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setIsUserModalOpen(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsUserModalOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm(t('common.messages.confirmDelete'))) {
      try {
        await users.delete(userId);
        setUsersList(usersList.filter(user => user._id !== userId));
      } catch (err) {
        setError(err.response?.data?.message || t('userManagement.errors.deleteUser'));
      }
    }
  };

  const handleSaveUser = async (userData) => {
    try {
      console.log("Saving user data:", userData); // Debug log
      
      // Create a clean copy of the data to send to the API
      const userDataToSend = {
        username: userData.username,
        name: userData.name,
        email: userData.email,
        role: userData.role,
        active: userData.active !== undefined ? userData.active : true // Explicitly set active to true if undefined
      };
      
      // Only add password for new users
      if (!selectedUser && userData.password) {
        userDataToSend.password = userData.password;
      }
      
      // Handle companyRole - ensure it's either a valid ID or null/undefined
      if (userData.companyRole) {
        // If companyRole is an object (from the dropdown), use its ID
        if (typeof userData.companyRole === 'object' && userData.companyRole._id) {
          userDataToSend.companyRole = userData.companyRole._id;
        }
        // If it's already a string ID, use it directly
        else if (typeof userData.companyRole === 'string' && userData.companyRole.trim() !== '') {
          userDataToSend.companyRole = userData.companyRole;
        }
        // Otherwise, don't include companyRole
      }
      
      console.log("Cleaned user data to send:", userDataToSend);
      
      if (selectedUser) {
        const { data } = await users.update(selectedUser._id, userDataToSend);
        setUsersList(usersList.map(user => user._id === data._id ? data : user));
      } else {
        const { data } = await users.create(userDataToSend);
        
        // Ensure we have the correct active state in the returned data
        // If data doesn't contain active property or it's different from what we sent
        const correctedData = {
          ...data,
          active: userDataToSend.active // Use the value we sent to the server
        };
        
        setUsersList([...usersList, correctedData]);
      }
      setIsUserModalOpen(false);
    } catch (err) {
      console.error("Error saving user:", err);
      console.error("Error details:", err.response?.data);
      setError(err.response?.data?.message || t('userManagement.errors.saveUser'));
    }
  };

  const handleCreateRole = () => {
    setSelectedRole(null);
    setIsRoleModalOpen(true);
  };

  const handleEditRole = (role) => {
    setSelectedRole(role);
    setIsRoleModalOpen(true);
  };

  const handleDeleteRole = async (roleId) => {
    if (window.confirm(t('common.messages.confirmDelete'))) {
      try {
        console.log("Deleting role with ID:", roleId);
        await companyRoles.delete(roleId);
        setCompanyRoles(prevRoles => prevRoles.filter(role => role._id !== roleId));
        setIsRoleModalOpen(false);
      } catch (error) {
        console.error('Error deleting company role:', error);
        setError('Failed to delete company role');
      }
    }
  };

  const handleSaveRole = async (roleData) => {
    try {
      console.log("Saving role data:", roleData);
      
      // Create a copy of the data without the _id field for new roles
      const dataToSend = {...roleData};
      if (!selectedRole) {
        // If creating a new role, delete any _id property that might exist
        delete dataToSend._id;
      }
      
      if (selectedRole) {
        // Update existing role
        const { data } = await companyRoles.update(selectedRole._id, dataToSend);
        setCompanyRoles(prevRoles => 
          prevRoles.map(role => role._id === data._id ? data : role)
        );
      } else {
        // Create new role
        const { data } = await companyRoles.create(dataToSend);
        setCompanyRoles(prevRoles => [...prevRoles, data]);
      }
      setIsRoleModalOpen(false);
    } catch (error) {
      console.error('Error saving company role:', error);
      setError('Failed to save company role');
    }
  };

  const getRoleLabel = (role) => {
    const roles = {
      admin: t('userManagement.roles.admin'),
      officeAdmin: t('userManagement.roles.officeAdmin'),
      warehouseAdmin: t('userManagement.roles.warehouseAdmin'),
      technician: t('userManagement.roles.technician')
    };
    return roles[role] || role;
  };

  const getCompanyRoleName = (companyRole) => {
    if (!companyRole) return '-';
    
    if (typeof companyRole === 'object' && companyRole.name) {
      return companyRole.name;
    }
    
    if (typeof companyRole === 'string') {
      const role = companyRolesList.find(r => r._id === companyRole);
      return role ? role.name : companyRole.substring(0, 8) + '...';
    }
    
    return '-';
  };

  const filteredUsers = usersList.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.username.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredRoles = companyRolesList.filter(role =>
    role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (role.description && role.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Page header */}
        <div className="px-4 sm:px-0 mb-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="mr-4 text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h2 className="text-2xl font-bold text-gray-900">{t('userManagement.title')}</h2>
            </div>
            
            {activeTab === 'users' ? (
              <button
                onClick={handleCreateUser}
                className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <UserPlus className="h-5 w-5 mr-2" />
                {t('userManagement.newUser')}
              </button>
            ) : (
              <button
                onClick={handleCreateRole}
                className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-5 w-5 mr-2" />
                {t('userManagement.newCompanyRole')}
              </button>
            )}
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-4">
            <nav className="-mb-px flex space-x-8">
              <button
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'users'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => setActiveTab('users')}
              >
                <UserPlus className="h-5 w-5 mr-2 inline-block" />
                {t('userManagement.usersTab')}
              </button>
              <button
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'roles'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => setActiveTab('roles')}
              >
                <Briefcase className="h-5 w-5 mr-2 inline-block" />
                {t('userManagement.companyRolesTab')}
              </button>
            </nav>
          </div>

          <div className="max-w-xs">
            <input
              type="text"
              placeholder={t('userManagement.search')}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{error}</h3>
              </div>
            </div>
          </div>
        )}

        {/* Users/Roles Table */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          {activeTab === 'users' ? (
            // Users Table
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('userManagement.name')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('userManagement.username')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('userManagement.email')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('userManagement.role')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('userManagement.companyRole')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('userManagement.status')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('common.actions.title')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user._id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{user.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{user.username}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {getRoleLabel(user.role)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.companyRole ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          {getCompanyRoleName(user.companyRole)}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {user.active ? t('common.status.active') : t('common.status.inactive')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEditUser(user)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                        title={t('common.actions.edit')}
                      >
                        <Edit className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user._id)}
                        className="text-red-600 hover:text-red-900"
                        title={t('common.actions.delete')}
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            // Company Roles Table
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('userManagement.roleName')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('userManagement.roleDescription')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('userManagement.usersWithRole')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('common.actions.title')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRoles.map((role) => {
                  const usersWithRole = usersList.filter(user => 
                    user.companyRole?._id === role._id || 
                    user.companyRole === role._id
                  );
                  return (
                    <tr key={role._id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{role.name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500 line-clamp-2">{role.description || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{usersWithRole.length} {t('userManagement.users').toLowerCase()}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEditRole(role)}
                          className="text-blue-600 hover:text-blue-900 mr-4"
                          title={t('common.actions.edit')}
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteRole(role._id)}
                          className="text-red-600 hover:text-red-900"
                          title={t('common.actions.delete')}
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {filteredRoles.length === 0 && (
                  <tr>
                    <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">
                      {t('userManagement.noRolesFound')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {activeTab === 'users' && filteredUsers.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">{t('userManagement.noUsersFound')}</p>
            </div>
          )}
        </div>
      </main>

      {/* User Modal */}
      {isUserModalOpen && (
        <UserModal
          isOpen={isUserModalOpen}
          onClose={() => setIsUserModalOpen(false)}
          user={selectedUser}
          onSave={handleSaveUser}
          companyRoles={companyRolesList}
        />
      )}

      {/* Company Role Modal */}
      {isRoleModalOpen && (
        <CompanyRoleModal
          isOpen={isRoleModalOpen}
          onClose={() => setIsRoleModalOpen(false)}
          role={selectedRole}
          onSave={handleSaveRole}
          onDelete={handleDeleteRole}
        />
      )}
    </div>
  );
};

export default UserManagementPage;